import { create } from "zustand";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";
import { PostUploadMediaType, UploadPostContentType } from "utils/enum/UploadPostContentType";
import { Service } from "utils/services";
import capitalize from "lodash.capitalize";
import axios from "axios";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Snackbar } from "components";
import { RouteApiLiveServer } from "route-apis/routeApiLiveServer";

dayjs.extend(utc);

interface DialogUploadPostState {
  uploadStep: "Attachments" | "Prepare";
  postContentType: UploadPostContentType;
  mediaType: PostUploadMediaType;
  pageLoading: AppRecordJK;
  userPost: any;
  professionals: any[];
  initializingFile: boolean;
  showLoader: boolean;
  textLoader: string;

  showError: boolean;
  textError: string;

  initializePage: (postContentType: UploadPostContentType) => Promise<void>;
  disposePage: () => Promise<void>;
  fetchUserInitPost: () => Promise<void>;
  setUploadStep: (step: "Attachments" | "Prepare") => Promise<void>;
  onTryAgain: () => Promise<void>;

  // * ---- Upload Video ---- * //
  isGenerateVideoThumbnail: boolean;
  videoUrl: string | null;
  videoThumbnails: string[];
  selectedVideoThumbnail: string | null;
  videoFileForTrim: File | null;

  onVideoUploaded: (file: File) => Promise<void>;
  onVideoTrimmed: (videoUrl: string, thumbnails: string[], music: any) => Promise<void>;
  setVideoFileForTrim: (file: File | null) => Promise<void>;
  setSelectedVideoThumbnail: (thumb: string) => Promise<void>;
  setVideoFileToTrim: () => Promise<void>;
  onChangeText: (value: string) => Promise<void>;

  // * ---- Upload Photos ---- * //
  viewPhotos: string[];
  uploadPhotos: any[];
  onPhotosUploaded: (files: File[]) => Promise<void>;

  // * ---- Upload Setting ---- * //
  description: string;
  isStory: boolean;
  price: string;
  layout: string;
  selectedPrivacies: string[];
  button: any;
  whoCanSeeThis: string;
  selectedLinkButton: string;
  link: string;
  iosLink: string;
  androidLink: string;

  eventTitle: string;
  eventDate: string;
  eventStartingTime: string;
  eventDuration: number;
  eventLocation: string;

  applyJobClosingDate: string;
  applyJobProfessional: string;

  pages: any[];
  pagesRecord: AppRecordJK;
  selectedPageIds: string[];

  setIsStory: (value: boolean) => Promise<void>;
  setPrice: (value: string) => Promise<void>;
  setLayout: (value: string) => Promise<void>;
  setWhoCanSeeThis: (value: string) => Promise<void>;
  toggleItemPricacy: (item: string) => Promise<void>;
  setSelectedLinkButton: (item: string) => Promise<void>;
  setLink: (value: string) => Promise<void>;
  setIosLink: (value: string) => Promise<void>;
  setAndroidLink: (value: string) => Promise<void>;
  fetchPages: (isRefresh: boolean) => Promise<void>;
  togglePage: (id: string) => Promise<void>;

  setEventTitle: (value: string) => void;
  setEventDate: (value: string) => void;
  setEventStartingTime: (value: string) => void;
  setEventDuration: (value: string) => void;
  setEventLocation: (value: string) => void;

  setApplyJobClosingDate: (value: string) => void;
  setApplyJobProfessional: (value: string) => void;

  // * ---- Music ---- * //
  music: any;

  setMusic: (music: any) => Promise<void>;

  // * ---- live ---- * //
  streamingKey: string | null;
  streamingUrl: string | null;
  isGettingStreamingKey: boolean;
  streamingHostCluster: any;
  isStreaming: boolean;
  streamingInfo: any;
  postLive: any;
  getStreamingKey: () => Promise<void>;
  setStreaming: (info: any) => Promise<void>;

  // * ---- Post Submit ---- * //

  onPostSubmit: (setIsDialogOpen: (value: boolean) => void, navigate: (path: string) => void) => Promise<void>;
}

const useDialogUploadPostStore = create<DialogUploadPostState>((set, get) => ({
  postContentType: UploadPostContentType.photoVideo,
  mediaType: PostUploadMediaType.photo,
  pageLoading: new AppRecordJK(),
  userPost: null,
  professionals: [],
  uploadStep: "Attachments",
  selectedLinkButton: "None",
  initializingFile: false,
  showLoader: false,
  textLoader: "",

  showError: false,
  textError: "",

  initializePage: async (uploadPostContentType: UploadPostContentType) => {
    try {
      set({
        postContentType: uploadPostContentType,
        mediaType: uploadPostContentType === UploadPostContentType.liveVideo ? PostUploadMediaType.live : PostUploadMediaType.photo,
        uploadStep: "Attachments",
        initializingFile: false,
        showLoader: uploadPostContentType === UploadPostContentType.liveVideo,
        textLoader: "",

        // * ---- Upload Photos ---- * //
        viewPhotos: [],
        uploadPhotos: [],

        // * ----Music ---- * //
        music: null,

        // * ---- Upload Setting ---- * //
        description: "",
        isStory: false,
        price: "0",
        layout: "default",
        selectedPrivacies: ["Allow Comment", "Allow Share", "Allow Add To Favourite"],
        button: null,
        whoCanSeeThis: "everyone",
        selectedLinkButton: "",
        link: "",
        iosLink: "",
        androidLink: "",

        eventTitle: "",
        eventDate: "",
        eventStartingTime: "",
        eventDuration: 0,
        eventLocation: "",

        applyJobClosingDate: "",
        applyJobProfessional: "",

        pages: [],
        pagesRecord: new AppRecordJK(),
        selectedPageIds: [],

        // * ---- Live ---- * //
        streamingKey: null,
        streamingUrl: null,
        streamingInfo: null,
        isGettingStreamingKey: false,
        streamingHostCluster: null,
        isStreaming: false,
        postLive: null,
      });

      await useDialogUploadPostStore.getState().fetchUserInitPost();
      await useDialogUploadPostStore.getState().fetchPages(true);

      if (uploadPostContentType === UploadPostContentType.liveVideo) {
        await useDialogUploadPostStore
          .getState()
          .getStreamingKey()
          .then((_) => {
            set({ showLoader: false });
          });
      }
    } catch (error) {
      AppLog.show({
        message: `[initializePage] initializePage error`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  disposePage: async () => {
    try {
      set({
        postContentType: UploadPostContentType.photoVideo,
        mediaType: PostUploadMediaType.photo,
        pageLoading: new AppRecordJK(),
        videoThumbnails: [],
        selectedVideoThumbnail: null,
        initializingFile: false,
        description: "",

        showLoader: false,
        textLoader: "",

        showError: false,
        textError: "",

        // * ----Music ---- * //
        music: null,

        // * ---- Live ---- * //
        streamingKey: null,
        streamingUrl: null,
        streamingInfo: null,
        isGettingStreamingKey: false,
        streamingHostCluster: null,
        isStreaming: false,
      });
    } catch (error) {
      AppLog.show({
        message: `[disposePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchUserInitPost: async () => {
    try {
      let url = RouteApi.getUserInitPost;
      url += `token=${Service.token}`;

      const response = await RequestApi(url);
      if (response.status === "success") {
        set({ userPost: response.user, music: response.music, professionals: response.professionals });
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchUserInitPost]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setUploadStep: async (step: "Attachments" | "Prepare") => {
    try {
      set({ uploadStep: step });
    } catch (error) {
      AppLog.show({
        message: `[setUploadStep]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onTryAgain: async () => {
    try {
      set({ showError: false, textError: "" });
    } catch (error) {
      AppLog.show({
        message: `[onTryAgain]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ---- Upload Photos ---- * //
  viewPhotos: [],
  uploadPhotos: [],

  onPhotosUploaded: async (files: File[]) => {
    try {
      set({
        initializingFile: true,
        showLoader: true,
        textLoader: "Initializing photos...",
      });
      const tmpPhotos: string[] = [];
      const tmpUploadPhotos: any[] = [];

      for (const file of files) {
        let convertedFile: File = file;
        if (file.type === "image/heic" || file.type === "image/heif" || file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")) {
          const heic = await Service.convertHeicToJpeg(file);
          if (heic) {
            convertedFile = heic;
            AppLog.log("Success Convert HEIC to JPEG");
          } else {
            throw new Error(`Convert HEIC to JPEG image failed`);
          }
        }

        if (convertedFile.size / 1024 > 500) {
          const imageCompress = await Service.compressImageForUpload(convertedFile);
          if (!imageCompress) throw new Error(`Compress image failed`);
          tmpPhotos.push(URL.createObjectURL(imageCompress));

          const imageDemension = await Service.getImageDimensionsFromFile(imageCompress);
          if (!imageDemension) throw new Error(`Get Demension image failed`);
          const { width: imageWidth, height: imageHeight } = imageDemension;

          AppLog.log("Success Image");

          const thumbnailCompress = await Service.compressImageToThumbnailForUpload(imageCompress);
          if (!thumbnailCompress) throw new Error(`Compress thumbnail image failed`);

          const thumbnailDemension = await Service.getImageDimensionsFromFile(thumbnailCompress);
          if (!thumbnailDemension) throw new Error(`Get Demension thumbnail failed`);
          const { width: thumbWidth, height: thumHeight } = thumbnailDemension;

          AppLog.log("Success Thumbnail");

          AppLog.log(`imageCompress : ${imageCompress.size / 2024} KB`);
          AppLog.log(`thumbnailCompress : ${thumbnailCompress.size / 2024} KB`);

          tmpUploadPhotos.push({
            image_file: imageCompress,
            image_width: imageWidth,
            image_height: imageHeight,
            thumbnail_file: thumbnailCompress,
            thumnail_width: thumbWidth,
            thumbnail_height: thumHeight,
          });
        } else {
          AppLog.log("[6] successs else");
          tmpPhotos.push(URL.createObjectURL(convertedFile));
          const imageDemensionData = await Service.getImageDimensionsFromFile(convertedFile);
          if (!imageDemensionData) throw new Error(`Demension image failed`);
          const { width: imageWidth, height: imageHeight } = imageDemensionData;

          const thumbnailCompress = await Service.compressImageToThumbnailForUpload(convertedFile);
          if (!thumbnailCompress) throw new Error(`Compress thumbnail image failed`);

          const thumbnailDemension = await Service.getImageDimensionsFromFile(thumbnailCompress);
          if (!thumbnailDemension) throw new Error(`Get Demension thumbnail failed`);
          const { width: thumbWidth, height: thumHeight } = thumbnailDemension;

          tmpUploadPhotos.push({
            image_file: convertedFile,
            image_width: imageWidth,
            image_height: imageHeight,
            thumbnail_file: thumbnailCompress,
            thumnail_width: thumbWidth,
            thumbnail_height: thumHeight,
          });
        }
      }

      set({
        initializingFile: false,
        viewPhotos: tmpPhotos,
        uploadPhotos: tmpUploadPhotos,
        mediaType: PostUploadMediaType.photo,
        uploadStep: "Prepare",
      });
    } catch (error) {
      set({
        initializingFile: false,
        showError: true,
        textError: "Uh-oh! Something went wrong. Please try again",
      });

      AppLog.show({
        message: `[onPhotosUploaded]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({
        showLoader: false,
        textLoader: "",
      });
    }
  },

  // * ---- Upload Video ---- * //
  description: "",
  isGenerateVideoThumbnail: false,
  videoUrl: null,
  videoThumbnails: [],
  selectedVideoThumbnail: null,
  videoFileForTrim: null,

  onVideoUploaded: async (file: File) => {
    try {
      set({ initializingFile: true, showLoader: true, textLoader: "Initializing video..." });

      const { userPost } = get();
      const url = URL.createObjectURL(file);
      const duration = await new Promise<number>((resolve) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.src = url;
        video.onloadedmetadata = () => resolve(video.duration || 0);
        video.onerror = () => resolve(0);
      });

      if (duration > Service.toInteger(userPost?.video_length_in_second)) {
        URL.revokeObjectURL(url); // cleanup
        set({
          videoFileForTrim: file,
        });
      } else {
        // * ---- Generate Thumbnails ---- * //
        set({
          isGenerateVideoThumbnail: true,
          textLoader: "Generating thumbnails ...",
        });

        // const blob = await fetch(url).then((res) => res.blob());
        // const file = new File([blob], "trimmed.mp4", { type: "video/mp4" });
        await Service.getThumbnails(file, 5).then((thumbnails) => {
          if (thumbnails) {
            set({
              videoUrl: url,
              isGenerateVideoThumbnail: false,
              videoThumbnails: thumbnails,
              selectedVideoThumbnail: thumbnails[0],
              uploadStep: "Prepare",
              mediaType: PostUploadMediaType.video,
            });
          }
        });
      }
    } catch (error) {
      set({
        isGenerateVideoThumbnail: false,
        initializingFile: false,
        showError: true,
        textError: "Uh-oh! Something went wrong. Please try again",
      });

      AppLog.show({
        message: `[onVideoUploaded]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ showLoader: false, textLoader: "" });
    }
  },

  onVideoTrimmed: async (videoUrl: string, thumbnails: string[], music: any) => {
    try {
      set({
        videoUrl: videoUrl,
        videoThumbnails: thumbnails,
        selectedVideoThumbnail: thumbnails[0],
        videoFileForTrim: null,
        uploadStep: "Prepare",
        mediaType: PostUploadMediaType.video,
        music: music,
      });
    } catch (error) {
      AppLog.show({
        message: `[onVideoTrimmed]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setVideoFileForTrim: async (file: File | null) => {
    try {
      set({
        videoFileForTrim: file,
        initializingFile: false,
      });
    } catch (error) {
      AppLog.show({
        message: `[setVideoFileForTrim]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setSelectedVideoThumbnail: async (thumb: string) => {
    try {
      set({ selectedVideoThumbnail: thumb });
    } catch (error) {
      AppLog.show({
        message: `[setSelectedVideoThumbnail]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setVideoFileToTrim: async () => {
    try {
      const { videoUrl } = get();
      const file = await Service.blobUrlToFile(videoUrl!, "video.mp4");

      set({
        videoFileForTrim: file,
      });
    } catch (error) {
      AppLog.show({
        message: `[setVideoFileToTrim]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onChangeText: async (value: string) => {
    try {
      set({
        description: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[onChangeText]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ---- Upload Setting ---- * //
  isStory: false,
  price: "0",
  layout: "default",
  selectedPrivacies: ["Allow Comment", "Allow Share", "Allow Add To Favourite"],
  button: null,
  whoCanSeeThis: "everyone",
  link: "",
  iosLink: "",
  androidLink: "",

  eventTitle: "",
  eventDate: "",
  eventStartingTime: "",
  eventDuration: 0,
  eventLocation: "",

  applyJobClosingDate: "",
  applyJobProfessional: "",

  pages: [],
  pagesRecord: new AppRecordJK(),
  selectedPageIds: [],

  setIsStory: async (value: boolean) => {
    try {
      set({
        isStory: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setIsStory]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setPrice: async (value: string) => {
    try {
      set({
        price: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setPrice]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setLayout: async (value: string) => {
    try {
      set({
        layout: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setLayout]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setWhoCanSeeThis: async (value: string) => {
    try {
      set({
        whoCanSeeThis: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setWhoCanSeeThis]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  toggleItemPricacy: async (item: string) => {
    try {
      set((state) => ({
        selectedPrivacies: state.selectedPrivacies.includes(item) ? state.selectedPrivacies.filter((i) => i !== item) : [...state.selectedPrivacies, item],
      }));
    } catch (error) {
      AppLog.show({
        message: `[toggleItemPricacy]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setSelectedLinkButton: async (item: string) => {
    try {
      set({
        selectedLinkButton: item,
      });
    } catch (error) {
      AppLog.show({
        message: `[setWhoCanSeeThis]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setLink: async (value: string) => {
    try {
      set({
        link: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setLink]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setIosLink: async (value: string) => {
    try {
      set({
        iosLink: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setIosLink]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setAndroidLink: async (value: string) => {
    try {
      set({
        androidLink: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setAndroidLink]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchPages: async (isRefresh: boolean) => {
    const { pages, pagesRecord } = get();

    let tmpPages = [...pages];
    let tmpPagesRecord = new AppRecordJK({
      ...pagesRecord,
    });

    try {
      let url: string = RouteApi.getPageUserPublished;
      url += `token=${Service.token}`;

      if (isRefresh) {
        tmpPages = [];
        tmpPagesRecord.loadingStatus = AppRecordStatusJK.loading;

        set({
          pages: tmpPages,
          pagesRecord: tmpPagesRecord,
        });
      } else {
        if (tmpPagesRecord.canLoadMore()) {
          url = tmpPagesRecord.nextPageUrl!;
          tmpPagesRecord.loadingStatus = AppRecordStatusJK.loadingMore;
          set({
            pagesRecord: tmpPagesRecord,
          });
        } else {
          return;
        }
      }

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const newPages = responses.pages;
        tmpPagesRecord.loadingStatus = newPages.next_page_url ? AppRecordStatusJK.loaded : AppRecordStatusJK.noLoadMore;
        tmpPagesRecord.nextPageUrl = newPages.next_page_url;

        if (isRefresh) {
          tmpPages.push(responses.current_user);
        }

        tmpPages = [...tmpPages, ...newPages.data];

        set({
          pages: tmpPages,
          pagesRecord: tmpPagesRecord,
        });

        if (isRefresh) {
          await useDialogUploadPostStore.getState().togglePage(`${tmpPages[0].id}`);
        }
      } else {
        tmpPagesRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({
          pagesRecord: tmpPagesRecord,
        });

        AppLog.show({
          message: responses.msg,
          status: StatusMsg.getType(responses.status),
        });
      }
    } catch (error) {
      tmpPagesRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({
        pagesRecord: tmpPagesRecord,
      });

      AppLog.show({
        message: `[fetchPages]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  togglePage: async (id: string) => {
    try {
      set((state) => {
        const alreadySelected = state.selectedPageIds.includes(id);

        if (alreadySelected && id === `${Service.userAuth.id}`) {
          return {}; // no update
        }

        return {
          selectedPageIds: alreadySelected ? state.selectedPageIds.filter((i) => i !== id) : [...state.selectedPageIds, id],
        };
      });
    } catch (error) {
      AppLog.show({
        message: `[togglePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setEventTitle: (value: string) => set({ eventTitle: value }),
  setEventDate: (value: string) => set({ eventDate: value }),
  setEventStartingTime: (value: string) => set({ eventStartingTime: value }),
  setEventDuration: (value: string) => set({ eventDuration: Service.toInteger(value) }),
  setEventLocation: (value: string) => set({ eventLocation: value }),

  setApplyJobClosingDate: (value: string) => set({ applyJobClosingDate: value }),
  setApplyJobProfessional: (value: string) => set({ applyJobProfessional: value }),

  // * ---- Music ---- * //
  music: null,

  setMusic: async (music: any) => {
    try {
      set({ music: music });
    } catch (error) {
      AppLog.show({
        message: `[setMusic]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ---- Live ---- * //
  streamingKey: null,
  streamingUrl: null,
  streamingInfo: null,
  isGettingStreamingKey: false,
  streamingHostCluster: null,
  isStreaming: false,
  postLive: null,

  getStreamingKey: async () => {
    try {
      set({
        isGettingStreamingKey: true,
      });

      let url = RouteApi.getStreamingHostCluster;
      url += `token=${Service.token}`;

      let responses = await RequestApi(url);
      if (responses.status === "success") {
        if (responses.post) {
          set({
            isGettingStreamingKey: false,
            streamingKey: null,
            streamingUrl: null,
            streamingHostCluster: null,
            isStreaming: false,
            postLive: responses.post,
          });
        } else {
          set({
            isGettingStreamingKey: false,
            streamingKey: responses.streaming_key,
            streamingUrl: responses.streaming_url,
            streamingHostCluster: responses.streaming_host_cluster,
            isStreaming: false,
            postLive: null,
          });

          let user = structuredClone(Service.userAuth);
          user.token = Service.token;
          user.authentication_key = Service.authenticationKey;
          user.streaming_key = responses.streaming_key;

          url = `${responses.streaming_host_cluster.host_name}${RouteApiLiveServer.postUser}`;
          url += `token=${Service.token}`;

          const data = {
            user: user,
          };

          responses = await RequestApi(url, { data: data });
          if (responses.status === "success") {
            AppLog.show({ message: responses.msg, status: StatusMsg.success });
          } else {
            AppLog.show({ message: responses.msg, status: StatusMsg.getType(responses.status) });
          }
        }
      } else {
        set({
          streamingKey: null,
          streamingUrl: null,
          streamingHostCluster: null,
        });
        AppLog.log(`${responses.msg}`);
      }
    } catch (error) {
      AppLog.show({
        message: `[getStreamingKey] `,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ isGettingStreamingKey: false });
    }
  },

  setStreaming: async (info: any) => {
    try {
      console.log("info : ", info);

      set({ isStreaming: info ?? false, streamingInfo: info });
    } catch (error) {
      AppLog.show({
        message: `[setStreaming]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ---- Post Submit ---- * //

  onPostSubmit: async (setIsDialogOpen: (value: boolean) => void, navigate: (path: string) => void) => {
    try {
      set({ showLoader: true, textLoader: "Uploading ..." });
      const {
        mediaType,
        description,
        selectedPrivacies,
        whoCanSeeThis,
        price,
        layout,
        isStory,
        selectedLinkButton,
        link,
        iosLink,
        androidLink,

        eventTitle,
        eventDate,
        eventStartingTime,
        eventDuration,
        eventLocation,

        applyJobClosingDate,
        applyJobProfessional,

        videoUrl,
        selectedVideoThumbnail,
        selectedPageIds,
        uploadPhotos,

        music,

        streamingHostCluster,
        streamingKey,
        streamingUrl,
        streamingInfo,
      } = get();
      const privacyYesNo = (key: string) => (selectedPrivacies.includes(key) ? "Yes" : "No");
      const utcNow = dayjs().utc().format("YYYY-MM-DD HH:mm:ss");
      let fileSize = 0.0;
      let objPost: Record<string, any> = {};
      objPost.user_id = `${Service.userAuth.id}`;
      objPost.multiple_page_post = "";
      objPost.media_type = mediaType.value;
      objPost.media_title = description;
      objPost.allow_comment = privacyYesNo("Allow Comment");
      objPost.allow_share = privacyYesNo("Allow Share");
      objPost.allow_add_to_favourite = privacyYesNo("Allow Add To Favourite");
      objPost.who_can_see_this = whoCanSeeThis;
      objPost.location = "";
      objPost.location_name = "";
      objPost.location_vicinity = "";
      objPost.location_type = "";
      objPost.city = "";
      objPost.country = "";
      objPost.location_latitude = "";
      objPost.location_longitude = "";
      objPost.is_location_saved = "";
      objPost.created_by = Service.userAuth.name;
      objPost.created_at = utcNow;
      objPost.synch_unid = await Service.getSynchID();
      objPost.synch_by = Service.userAuth.name;
      objPost.synch_status = "New";
      objPost.synch_at = utcNow;

      if (music) {
        objPost.audio_path = "";
        objPost.audio_type = "AUDIO_URL";
        objPost.sound_title = music.sound_title;
        objPost.sound_author = music.sound_author;
        objPost.sound_duration = music.sound_duration;
        objPost.sound_url = music.sound_url;
        objPost.sound_src = music.sound_src;
        objPost.sound_id = music.sound_id;
        objPost.sound_picture_url = music.sound_picture_url;
      }

      // Cart not yet to implement
      objPost.is_enable_cart = "No";
      objPost.unit_price = Service.toDouble(price);
      objPost.item_group = "";
      // Status
      objPost.media_style = "";
      // Sound
      objPost.sound_starting_at = 0;
      objPost.sound_ending_at = 0;
      objPost.provider_status_type = isStory ? "added_story" : "";
      objPost.link_button = selectedLinkButton === "None" ? "" : selectedLinkButton;
      objPost.link_url = link;
      objPost.link_ios_url = iosLink;
      objPost.link_android_url = androidLink;

      if (selectedLinkButton === "Join Event") {
        objPost.event_title = eventTitle;
        objPost.event_date = eventDate;
        objPost.event_time = eventStartingTime;
        objPost.event_duration = eventDuration;
        objPost.event_location = eventLocation;

        const urlObj = new URL(eventLocation);
        const qParam = urlObj.searchParams.get("q");
        const llParam = urlObj.searchParams.get("ll");
        const coordString = qParam || llParam;

        if (coordString) {
          const [lat, lon] = coordString.split(",").map(Number);
          objPost.event_location_latitude = lat;
          objPost.event_location_longitude = lon;
        } else {
          objPost.event_location_latitude = 0;
          objPost.event_location_longitude = 0;
        }

        objPost.event_city = "";
        objPost.event_country = "";
      } else if (selectedLinkButton === "Apply Job") {
        objPost.closing_date = applyJobClosingDate;
        objPost.professional = applyJobProfessional;
      }

      if (mediaType === PostUploadMediaType.video) {
        // Get Video Duration
        const { duration: videoDuration, width: videoWidth, height: videoHeight } = await Service.getVideoMetadata(videoUrl!);
        objPost.video_duration = videoDuration;
        let attachment: Record<string, any> = {};
        attachment.attachment_media_width = videoWidth;
        attachment.attachment_media_height = videoHeight;
        attachment.attachment_media_ratio = videoWidth / videoHeight;
        attachment.path_video_image = selectedVideoThumbnail;
        attachment.attachment_media_volume = 100;
        const { width: imgWidth, height: imgheight } = await Service.getImageDimensionsFromUrl(selectedVideoThumbnail!);
        attachment.attachment_thumbnail_width = imgWidth;
        attachment.attachment_thumbnail_height = imgheight;
        const videoFile = await Service.blobUrlToFile(videoUrl!, "video.mp4");
        const thumbnailFile = await Service.blobUrlToFile(selectedVideoThumbnail!, "thumbnail.jpeg");
        const smallThumbnailFile = await Service.compressJpeg(thumbnailFile);
        if (!smallThumbnailFile) {
          return;
        }
        fileSize = videoFile.size / 1024;
        fileSize += thumbnailFile.size / 1024;
        fileSize += smallThumbnailFile.size / 1024;
        // GET STORAGE
        let url = RouteApi.getStorageHostCluster;
        url += `token=${Service.token}`;
        url += `&file_size=${fileSize}`;
        let responses = await RequestApi(url);
        if (responses.status === "success") {
          const storageHostClusterId: string = `${responses.storage_host_cluster.id}`;
          const hostNameStorage: string = `${responses.storage_host_cluster.host_name}`;
          url = `${hostNameStorage}${RouteApi.submitUploadFile}`;
          url += `token=${Service.token}`;
          url += `&storage_host_cluster_id=${storageHostClusterId}`;
          // UPLOAD FILE HAVE 3 FILES video, thumbnail and small thumbnail
          const videoKey: string = await Service.getSynchID();
          const thumbnailKey: string = await Service.getSynchID();
          const smallThumbnailKey = await Service.getSynchID();
          attachment.video_upload_key = videoKey;
          attachment.image_upload_key = thumbnailKey;
          attachment.thubnail_upload_key = smallThumbnailKey;
          const formData = new FormData();
          formData.append(videoKey, videoFile);
          formData.append(thumbnailKey, thumbnailFile);
          formData.append(smallThumbnailKey, smallThumbnailFile);
          responses = await axios.post(url, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
              set({ textLoader: `Uploading... ${percent}%` });
            },
          });
          if (responses.status === 200) {
            responses = responses.data;
            if (responses.status === "success") {
              const uploadKeys: string[] = responses.upload_keys;
              const uploadFiles: any[] = responses.upload_files;
              // VIDEO
              let index: number = uploadKeys.indexOf(videoKey);
              if (index > -1) {
                attachment.attachment_source = uploadFiles[index].attachment_source;
                attachment.attachment_url = uploadFiles[index].attachment_url;
              }
              // IMAGE
              index = uploadKeys.indexOf(thumbnailKey);
              if (index > -1) {
                attachment.attachment_media_picture_file_source = uploadFiles[index].attachment_source;
                attachment.attachment_media_picture_file_src = uploadFiles[index].attachment_url;
              }
              // GIF -> Image Thumbnail
              index = uploadKeys.indexOf(smallThumbnailKey);
              if (index > -1) {
                attachment.attachment_thumbnail_source = uploadFiles[index].attachment_source;
                attachment.attachment_thumbnail_url = uploadFiles[index].attachment_url;
              }
              objPost.provider_storage_host_id = storageHostClusterId;
              objPost.attachments = [attachment];
              let tmpPostUpload: any[] = [];
              for (const pageId of selectedPageIds) {
                tmpPostUpload.push({
                  ...objPost,
                  user_id: pageId,
                });
              }
              const posts = { posts: tmpPostUpload };
              // SUBMIT POST
              url = RouteApi.submitPost;
              url += `token=${Service.token}`;
              responses = await RequestApi(url, { data: posts });
              if (responses.status === "success") {
                setIsDialogOpen(false);
                Snackbar({ message: responses.msg, status: StatusMsg.success });
              } else {
                AppLog.show({
                  message: responses.msg,
                  status: StatusMsg.getType(responses.status),
                });
              }
            } else {
              AppLog.show({
                message: responses.msg,
                status: StatusMsg.getType(responses.status),
              });
            }
          } else {
            console.log("responses 3 : ", responses);
          }
        } else {
          AppLog.show({
            message: responses.msg,
            status: StatusMsg.getType(responses.status),
          });
        }
      } else if (mediaType === PostUploadMediaType.photo) {
        objPost.media_template = capitalize(layout);
        let attachments: any[] = [];
        const formData = new FormData();

        for (const element of uploadPhotos) {
          let attachment: Record<string, any> = {};
          attachment.id = await Service.getSynchID();
          attachment.attachment_media_width = element.image_width;
          attachment.attachment_media_height = element.image_height;
          attachment.attachment_media_ratio = element.image_height / element.image_width;
          attachment.attachment_thumbnail_width = element.thumnail_width;
          attachment.attachment_thumbnail_height = element.thumbnail_height;

          attachment.image_upload_key = await Service.getSynchID();
          attachment.thubnail_upload_key = await Service.getSynchID();

          fileSize += element.image_file / 1024;
          fileSize += element.thumbnail_file / 1024;
          formData.append(attachment.image_upload_key, element.image_file);
          formData.append(attachment.thubnail_upload_key, element.thumbnail_file);
          attachments.push(attachment);
        }

        // GET STORAGE
        let url = RouteApi.getStorageHostCluster;
        url += `token=${Service.token}`;
        url += `&file_size=${fileSize}`;
        let responses = await RequestApi(url);
        if (responses.status === "success") {
          const storageHostClusterId: string = `${responses.storage_host_cluster.id}`;
          const hostNameStorage: string = `${responses.storage_host_cluster.host_name}`;
          url = `${hostNameStorage}${RouteApi.submitUploadFile}`;
          url += `token=${Service.token}`;
          url += `&storage_host_cluster_id=${storageHostClusterId}`;
          responses = await axios.post(url, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
              set({ textLoader: `Uploading... ${percent}%` });
            },
          });
          if (responses.status === 200) {
            responses = responses.data;
            if (responses.status === "success") {
              const uploadKeys: string[] = responses.upload_keys;
              const uploadFiles: any[] = responses.upload_files;
              for (let att of attachments) {
                let index: number = uploadKeys.indexOf(att.image_upload_key);
                if (index > -1) {
                  att.attachment_source = uploadFiles[index].attachment_source;
                  att.attachment_url = uploadFiles[index].attachment_url;
                }
                index = uploadKeys.indexOf(att.thubnail_upload_key);
                if (index > -1) {
                  att.attachment_thumbnail_source = uploadFiles[index].attachment_source;
                  att.attachment_thumbnail_url = uploadFiles[index].attachment_url;
                }
              }
              objPost.provider_storage_host_id = storageHostClusterId;
              objPost.attachments = attachments;
              let tmpPostUpload: any[] = [];
              for (const pageId of selectedPageIds) {
                tmpPostUpload.push({
                  ...objPost,
                  user_id: pageId,
                });
              }
              const posts = { posts: tmpPostUpload };
              // SUBMIT POST
              url = RouteApi.submitPost;
              url += `token=${Service.token}`;
              responses = await RequestApi(url, { data: posts });
              if (responses.status === "success") {
                setIsDialogOpen(false);
                Snackbar({ message: responses.msg, status: StatusMsg.success });
              } else {
                AppLog.show({
                  message: responses.msg,
                  status: StatusMsg.getType(responses.status),
                });
              }
            } else {
              AppLog.show({
                message: responses.msg,
                status: StatusMsg.getType(responses.status),
              });
            }
          } else {
            console.log("responses 33 : ", responses);
          }
        } else {
          AppLog.show({
            message: responses.msg,
            status: StatusMsg.getType(responses.status),
          });
        }
      } else if (mediaType === PostUploadMediaType.live) {
        let attachment: Record<string, any> = {};
        attachment.attachment_media_width = streamingInfo.width;
        attachment.attachment_media_height = streamingInfo.height;
        attachment.attachment_media_ratio = 1;
        attachment.attachment_media_volume = 100;
        attachment.attachment_thumbnail_width = streamingInfo.width;
        attachment.attachment_thumbnail_height = streamingInfo.height;

        const storageHostClusterId = streamingHostCluster.id;
        objPost.provider_storage_host_id = storageHostClusterId;
        objPost.attachments = [attachment];

        objPost.media_key = streamingKey;
        objPost.media_url = streamingUrl;
        objPost.media_host_name = streamingHostCluster.host_name;

        let tmpPostUpload: any[] = [];
        for (const pageId of selectedPageIds) {
          tmpPostUpload.push({
            ...objPost,
            user_id: pageId,
          });
        }
        const posts = { posts: tmpPostUpload };

        // * SUBMIT TO LIVE SERVER
        let url = `${streamingHostCluster.host_name}${RouteApiLiveServer.postGoLive}`;
        url += `token=${Service.token}`;
        url += `&streaming_key=${streamingKey}`;

        let response = await RequestApi(url);
        if (response.status === "success") {
          // SUBMIT POST
          url = RouteApi.submitPost;
          url += `token=${Service.token}`;
          let responses = await RequestApi(url, { data: posts });

          if (responses.status === "success") {
            setIsDialogOpen(false);
            Snackbar({ message: "Streaming is start live...", status: StatusMsg.success });

            setTimeout(() => {
              navigate("/live-studio");
            }, 3000);
          } else {
            AppLog.show({
              message: responses.msg,
              status: StatusMsg.getType(responses.status),
            });
          }
        } else {
          AppLog.show({
            message: response.msg,
            status: StatusMsg.getType(response.status),
          });
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[onPostSubmit]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ showLoader: false, textLoader: "" });
    }
  },
}));

export default useDialogUploadPostStore;

